import countryList from 'react-select-country-list';
import { ATTRIBUTE_MAPPING, getNationalityCode } from '../utils.js';

// Overall is a percentile rank (1-99, davies_percentile_v1 model); bands per
// design: gold 86+, silver 81-85, bronze 80 or below.
export const TIER_THRESHOLDS = Object.freeze({
  gold: 86,
  silver: 81,
});

export const TIER = TIER_THRESHOLDS;

const COUNTRIES = countryList();

const POSITION_GROUP_BY_CODE = Object.freeze({
  GK: 'GKP',
  GKP: 'GKP',
  GOALKEEPER: 'GKP',
  CB: 'DEF',
  LB: 'DEF',
  RB: 'DEF',
  LCB: 'DEF',
  RCB: 'DEF',
  LWB: 'DEF',
  RWB: 'DEF',
  SW: 'DEF',
  DF: 'DEF',
  DEF: 'DEF',
  DEFENDER: 'DEF',
  CM: 'MID',
  CDM: 'MID',
  CAM: 'MID',
  DM: 'MID',
  AM: 'MID',
  LM: 'MID',
  RM: 'MID',
  MF: 'MID',
  MD: 'MID',
  MID: 'MID',
  MIDFIELDER: 'MID',
  ST: 'FWD',
  CF: 'FWD',
  SS: 'FWD',
  LF: 'FWD',
  RF: 'FWD',
  LW: 'FWD',
  RW: 'FWD',
  FW: 'FWD',
  FWD: 'FWD',
  FORWARD: 'FWD',
});

const GENERIC_POSITION_CODES = new Set([
  'DF',
  'DEF',
  'DEFENDER',
  'MF',
  'MD',
  'MID',
  'MIDFIELDER',
  'FW',
  'FWD',
  'FORWARD',
]);

function toFiniteNumber(value) {
  if (value === null || value === undefined || value === '') return null;

  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function toRoundedNumber(value) {
  const number = toFiniteNumber(value);
  return number === null ? null : Math.round(number);
}

function normalizeCode(value) {
  if (value === null || value === undefined) return null;

  const code = String(value).trim().toUpperCase();
  return code || null;
}

export function groupOf(value) {
  const code = normalizeCode(value);
  if (!code) return null;

  if (POSITION_GROUP_BY_CODE[code]) {
    return POSITION_GROUP_BY_CODE[code];
  }
  if (code.includes('GOALKEEP')) return 'GKP';
  if (code.includes('DEFEND')) return 'DEF';
  if (code.includes('MIDFIELD')) return 'MID';
  if (code.includes('FORWARD') || code.includes('STRIKER')) return 'FWD';

  return null;
}

function getPositionLabel(explicitPosition, roleCode2, positionGroup) {
  const explicitCode = normalizeCode(explicitPosition);

  if (explicitCode === 'GK' || explicitCode === 'GKP' || explicitCode === 'GOALKEEPER') {
    return 'GK';
  }
  if (explicitCode && GENERIC_POSITION_CODES.has(explicitCode)) {
    return groupOf(explicitCode) || positionGroup;
  }
  if (explicitCode) {
    return explicitCode;
  }

  const roleCode = normalizeCode(roleCode2);
  if (roleCode === 'GK' || roleCode === 'GKP') {
    return 'GK';
  }
  if (roleCode && !GENERIC_POSITION_CODES.has(roleCode)) {
    return roleCode;
  }

  return positionGroup;
}

function normalizeFlagCode(nationalityName, nationalityCode) {
  const resolvedCode = getNationalityCode(nationalityName, nationalityCode);
  if (typeof resolvedCode === 'string' && resolvedCode.trim().length === 2) {
    return resolvedCode.trim().toUpperCase();
  }

  if (typeof nationalityName !== 'string' || !nationalityName.trim()) return null;
  return COUNTRIES.getValue(nationalityName.trim()) || null;
}

function statValue(source, attributes, shortKey, flatKey, attributeKey) {
  return toRoundedNumber(
    source.stats?.[shortKey]
      ?? source[flatKey]
      ?? attributes?.[attributeKey]
  );
}

export function tierOf(overall) {
  const value = toFiniteNumber(overall);
  if (value === null) return null;
  if (value >= TIER_THRESHOLDS.gold) return 'gold';
  if (value >= TIER_THRESHOLDS.silver) return 'silver';
  return 'bronze';
}

export function normalizeCardPlayer(input = {}) {
  const source = input || {};
  const profile = source.profile || {};
  const role = source.role || profile.role || {};
  const attributes = source.attributes?.attributes || source.attributes || {};
  const explicitPosition = source.positionLabel
    ?? source.position
    ?? source.slotPosition
    ?? source.title
    ?? source.currentPosition
    ?? null;

  const positionGroup = groupOf(role.code3)
    || groupOf(source.positionGroup)
    || groupOf(role.code2)
    || groupOf(role.name)
    || groupOf(explicitPosition);
  const isGoalkeeper = source.isGoalkeeper === true
    || normalizeCode(role.code2) === 'GK'
    || normalizeCode(role.code3) === 'GKP'
    || groupOf(explicitPosition) === 'GKP'
    || positionGroup === 'GKP';
  const nationalityName = source.nationalityName
    ?? source.nationality
    ?? profile.birthArea?.name
    ?? profile.passportArea?.name
    ?? null;
  const nationalityCode = source.nationalityCode
    ?? profile.birthArea?.alpha2code
    ?? profile.passportArea?.alpha2code
    ?? null;
  const averageRating = toFiniteNumber(source.averageRating);

  const stats = isGoalkeeper
    ? {
        sho: statValue(source, attributes, 'sho', 'shotStopping', ATTRIBUTE_MAPPING.shotStopping),
        pas: statValue(source, attributes, 'pas', 'gkPassing', ATTRIBUTE_MAPPING.passing),
        agg: statValue(source, attributes, 'agg', 'gkAggressiveness', ATTRIBUTE_MAPPING.aggressiveness),
      }
    : {
        cro: statValue(source, attributes, 'cro', 'crossing', ATTRIBUTE_MAPPING.crossing),
        dri: statValue(source, attributes, 'dri', 'dribbling', ATTRIBUTE_MAPPING.dribbling),
        fin: statValue(source, attributes, 'fin', 'finishing', ATTRIBUTE_MAPPING.finishing),
        cre: statValue(source, attributes, 'cre', 'creativity', ATTRIBUTE_MAPPING.creativity),
        pas: statValue(source, attributes, 'pas', 'buildUpPassing', ATTRIBUTE_MAPPING.buildupPassing),
        aer: statValue(source, attributes, 'aer', 'aerialAbility', ATTRIBUTE_MAPPING.aerialAbility),
        def: statValue(source, attributes, 'def', 'defensiveAwareness', ATTRIBUTE_MAPPING.defensiveAwareness),
        win: statValue(source, attributes, 'win', 'ballWinning', ATTRIBUTE_MAPPING.ballWinning),
      };

  return {
    wyId: source.wyId ?? profile.wyId ?? null,
    name: source.name || profile.shortName || profile.name || '',
    positionLabel: getPositionLabel(explicitPosition, role.code2, positionGroup),
    positionGroup: isGoalkeeper ? 'GKP' : positionGroup,
    nationalityName,
    nationalityCode: normalizeFlagCode(nationalityName, nationalityCode),
    age: toRoundedNumber(source.age ?? profile.age),
    overall: toRoundedNumber(source.overall),
    overallSource: typeof source.overallSource === 'string' ? source.overallSource : null,
    rating: averageRating !== null && averageRating > 0 ? averageRating : null,
    isGoalkeeper,
    stats,
  };
}
